'use client';

import Link from 'next/link';
import { useEffect, useState, useCallback, useRef } from 'react';
import { Search, MapPin, Briefcase, Filter, ChevronLeft, ChevronRight, ChevronDown, X } from 'lucide-react';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { useTranslation } from '@/hooks/useTranslation';
import VerificationBadge from '@/components/VerificationBadge';

interface PublicFreelancer {
  id: string;
  profileSlug: string;
  firstName: string;
  lastName: string;
  headline?: string;
  profilePhoto?: string;
  coverPhoto?: string;
  country?: string;
  city?: string;
  isAvailableForWork: boolean;
  isVerifiedProfessional: boolean;
  salaryExpectationMin?: number;
  salaryExpectationMax?: number;
  salaryCurrency: string;
  skills: { skill: { name: string } }[];
  portfolio: { id: string; title: string; imageUrl?: string }[];
  _count: { portfolio: number; experience: number };
}

// Fallback lists (Portuguese) used before translations load
const SKILL_OPTIONS_FALLBACK = [
  'Administrador de Sistemas', 'Administração', 'Análise de Dados', 'Análise de Negócios',
  'Análise Financeira', 'Arquitetura', 'Assistente Administrativo', 'Assistente Virtual',
  'Atendimento ao Cliente', 'Auditoria', 'Automação', 'Banco de Dados', 'Blockchain',
  'Branding', 'Ciência de Dados', 'Cloud Computing', 'Computação em Nuvem', 'Consultoria',
  'Contabilidade', 'Copywriting', 'Cibersegurança', 'Desenvolvimento Android',
  'Desenvolvimento Back-end', 'Desenvolvimento Flutter', 'Desenvolvimento Front-end',
  'Desenvolvimento Full Stack', 'Desenvolvimento iOS', 'Desenvolvimento Laravel',
  'Desenvolvimento Mobile', 'Desenvolvimento Node.js', 'Desenvolvimento PHP',
  'Desenvolvimento React', 'Desenvolvimento React Native', 'Desenvolvimento Vue.js',
  'Desenvolvimento Web', 'Design de Interiores', 'Design de Logotipos', 'Design de Moda',
  'Design Gráfico', 'DevOps', 'Direito', 'E-commerce', 'Edição de Áudio', 'Edição de Vídeo',
  'Educação Online', 'Engenharia', 'Engenharia Civil', 'Engenharia Elétrica',
  'Engenharia Mecânica', 'Entrada de Dados', 'Escrita Técnica', 'Excel', 'Fotografia',
  'Gestão Comercial', 'Gestão de Projetos', 'Gestão de Redes', 'Gestão de Recursos Humanos',
  'Ilustração', 'Inteligência Artificial', 'Java', 'JavaScript', 'Laravel', 'Machine Learning',
  'Marketing', 'Marketing Digital', 'Microsoft Office', 'Motion Design', 'Power BI',
  'Programação', 'Programação Python', 'Programação SQL', 'QA/Testes de Software',
  'Recrutamento', 'Redação de Conteúdo', 'Redes Informáticas', 'SEO', 'Shopify',
  'Social Media', 'Suporte Técnico', 'Tradução', 'UI/UX Design', 'Vendas', 'WordPress',
];

const COUNTRY_OPTIONS_FALLBACK = [
  'Angola', 'Portugal', 'Brasil', 'Moçambique', 'Cabo Verde',
];

// Indices of the 5 quick-access skill chips (into the options array)
const QUICK_SKILL_INDICES = [38, 34, 83, 66, 72]; // Design Gráfico, Desenvolvimento Web, UI/UX Design, Marketing Digital, Programação Python

// ── Searchable combobox ───────────────────────────────────────────────────────

interface SearchableSelectProps {
  options: string[];
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  allLabel: string;
}

function SearchableSelect({ options, value, onChange, placeholder, allLabel }: SearchableSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const filtered = query.trim()
    ? options.filter(o => o.toLowerCase().includes(query.toLowerCase()))
    : options;

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const select = (v: string) => {
    onChange(v);
    setOpen(false);
    setQuery('');
  };

  const displayLabel = value || allLabel;

  return (
    <div className="relative" ref={containerRef}>
      {/* Trigger */}
      <button
        type="button"
        onClick={() => { setOpen(o => !o); setTimeout(() => inputRef.current?.focus(), 50); }}
        className="w-full flex items-center justify-between border border-gray-200 rounded-xl px-4 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-teal-500 hover:border-teal-400 transition"
      >
        <span className={value ? 'text-gray-900 font-medium' : 'text-gray-400'}>{displayLabel}</span>
        <div className="flex items-center gap-1 flex-shrink-0 ml-2">
          {value && (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => { e.stopPropagation(); select(''); }}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); select(''); } }}
              className="p-0.5 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600 cursor-pointer"
            >
              <X className="w-3.5 h-3.5" />
            </span>
          )}
          <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
        </div>
      </button>

      {/* Dropdown */}
      {open && (
        <div className="absolute z-50 left-0 right-0 top-full mt-1 bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden">
          {/* Search input */}
          <div className="p-2 border-b border-gray-100">
            <div className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2">
              <Search className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder={placeholder}
                className="bg-transparent outline-none text-sm w-full text-gray-900 placeholder:text-gray-400"
              />
              {query && (
                <button onClick={() => setQuery('')} className="text-gray-400 hover:text-gray-600">
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>

          {/* Options list */}
          <div className="max-h-56 overflow-y-auto overscroll-contain">
            {/* "All" option */}
            <button
              type="button"
              onClick={() => select('')}
              className={`w-full text-left px-4 py-2.5 text-sm transition hover:bg-teal-50 ${!value ? 'bg-teal-50 text-teal-700 font-semibold' : 'text-gray-500'}`}
            >
              {allLabel}
            </button>

            {filtered.length === 0 ? (
              <p className="text-center text-sm text-gray-400 py-6">Sem resultados</p>
            ) : (
              filtered.map(opt => (
                <button
                  key={opt}
                  type="button"
                  onClick={() => select(opt)}
                  className={`w-full text-left px-4 py-2.5 text-sm transition hover:bg-teal-50 ${value === opt ? 'bg-teal-50 text-teal-700 font-semibold' : 'text-gray-700'}`}
                >
                  {opt}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export default function PortfoliosPage() {
  const { t, translations } = useTranslation();
  const [freelancers, setFreelancers] = useState<PublicFreelancer[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');
  const [skill, setSkill] = useState('');
  const [country, setCountry] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const limit = 12;

  // Pull lists from translations (falls back to Portuguese constants)
  const portfoliosTrans = (translations as Record<string, unknown>)?.portfolios as Record<string, unknown> | undefined;
  const SKILL_OPTIONS = (portfoliosTrans?.skillOptions as string[] | undefined) ?? SKILL_OPTIONS_FALLBACK;
  const COUNTRY_OPTIONS = (portfoliosTrans?.countryOptions as string[] | undefined) ?? COUNTRY_OPTIONS_FALLBACK;
  // Quick chips: pick 5 by index so they also switch language
  const QUICK_SKILLS = QUICK_SKILL_INDICES.map(i => SKILL_OPTIONS[i]).filter(Boolean) as string[];

  const load = useCallback((qv = q, sv = skill, cv = country, pg = page) => {
    setLoading(true);
    const p = new URLSearchParams({ limit: String(limit), page: String(pg) });
    if (qv) p.set('q', qv);
    if (sv) p.set('skill', sv);
    if (cv) p.set('country', cv);
    fetch(`/api/v1/public/freelancers?${p}`)
      .then(r => r.json())
      .then(j => {
        if (j.success) {
          setFreelancers(j.data.freelancers ?? []);
          setTotal(j.data.total ?? 0);
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [q, skill, country, page]); // eslint-disable-line

  useEffect(() => { load(); }, []); // eslint-disable-line

  const search = () => { setPage(1); load(q, skill, country, 1); };
  const totalPages = Math.ceil(total / limit);
  const activeFilterCount = [skill, country].filter(Boolean).length;

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      {/* Hero */}
      <section className="bg-gradient-to-r from-blue-900 via-teal-700 to-teal-600 text-white py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl font-bold mb-4">{t('portfolios.heroTitle') || 'Talento Autônomo em Angola'}</h1>
          <p className="text-lg sm:text-xl opacity-90 mb-8">{t('portfolios.heroSub') || 'Descubra os melhores profissionais disponíveis para os seus projectos'}</p>

          {/* Search bar */}
          <div className="bg-white rounded-2xl p-2 flex gap-2 max-w-2xl mx-auto shadow-lg">
            <div className="flex items-center gap-2 flex-1 px-3">
              <Search className="w-5 h-5 text-gray-400 flex-shrink-0" />
              <input
                className="outline-none text-gray-900 text-sm w-full py-2 bg-transparent placeholder:text-gray-400"
                placeholder={t('portfolios.searchPlaceholder') || 'Pesquisar por nome, skill, localização...'}
                value={q}
                onChange={e => setQ(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && search()}
              />
            </div>
            <button
              onClick={search}
              className="bg-teal-600 text-white px-6 py-2.5 rounded-xl text-sm font-semibold hover:bg-teal-700 transition flex-shrink-0"
            >
              {t('portfolios.searchBtn') || 'Pesquisar'}
            </button>
          </div>
        </div>
      </section>

      {/* Filters + Results */}
      <main className="max-w-7xl mx-auto px-4 py-10">
        {/* Filter bar */}
        <div className="flex flex-wrap items-center gap-3 mb-8">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-4 py-2.5 border rounded-xl text-sm font-semibold transition shadow-sm ${
              showFilters
                ? 'bg-teal-600 text-white border-teal-600'
                : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
            }`}
          >
            <Filter className="w-4 h-4" />
            {t('portfolios.filters') || 'Filtros'}{activeFilterCount > 0 && ` (${activeFilterCount})`}
          </button>

          {/* Quick skill chips */}
          {QUICK_SKILLS.map(s => (
            <button
              key={s}
              onClick={() => { const ns = skill === s ? '' : s; setSkill(ns); setPage(1); load(q, ns, country, 1); }}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold transition border ${skill === s ? 'bg-teal-600 text-white border-teal-600' : 'bg-white text-gray-600 border-gray-200 hover:border-teal-400'}`}
            >
              {s}
            </button>
          ))}

          {(skill || country || q) && (
            <button onClick={() => { setSkill(''); setCountry(''); setQ(''); setPage(1); load('', '', '', 1); }}
              className="px-3 py-1.5 rounded-full text-xs font-semibold text-red-600 border border-red-200 bg-red-50 hover:bg-red-100 transition">
              {t('portfolios.clearFilters') || 'Limpar filtros'} ×
            </button>
          )}

          <span className="ml-auto text-sm text-gray-500">{total} {t('portfolios.profilesFound') || 'perfis encontrados'}</span>
        </div>

        {/* Expanded filters with searchable dropdowns */}
        {showFilters && (
          <div className="bg-white border border-gray-200 rounded-2xl p-5 mb-6 grid grid-cols-1 sm:grid-cols-2 gap-4 shadow-sm">
            <div>
              <label className="text-xs font-bold text-gray-600 mb-2 block">{t('portfolios.filterSkill') || 'Skill Principal'}</label>
              <SearchableSelect
                options={SKILL_OPTIONS}
                value={skill}
                onChange={setSkill}
                placeholder="Pesquisar skill..."
                allLabel={t('portfolios.allSkills') || 'Todas as skills'}
              />
            </div>
            <div>
              <label className="text-xs font-bold text-gray-600 mb-2 block">{t('portfolios.filterCountry') || 'País'}</label>
              <SearchableSelect
                options={COUNTRY_OPTIONS}
                value={country}
                onChange={setCountry}
                placeholder="Pesquisar país..."
                allLabel={t('portfolios.allCountries') || 'Todos os países'}
              />
            </div>
            <div className="sm:col-span-2 flex justify-end gap-3">
              <button onClick={() => { setSkill(''); setCountry(''); }} className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-xl hover:bg-gray-50">{t('portfolios.clear') || 'Limpar'}</button>
              <button onClick={() => { setShowFilters(false); search(); }} className="px-6 py-2 bg-teal-600 text-white text-sm font-semibold rounded-xl hover:bg-teal-700">{t('portfolios.apply') || 'Aplicar'}</button>
            </div>
          </div>
        )}

        {/* Grid */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
            {Array.from({ length: 12 }).map((_, i) => (
              <div key={i} className="bg-white rounded-2xl border border-gray-200 overflow-hidden animate-pulse">
                <div className="h-40 bg-gray-100" />
                <div className="p-4 space-y-2">
                  <div className="h-4 bg-gray-100 rounded w-3/4" />
                  <div className="h-3 bg-gray-100 rounded w-1/2" />
                  <div className="h-3 bg-gray-100 rounded w-full" />
                </div>
              </div>
            ))}
          </div>
        ) : freelancers.length === 0 ? (
          <div className="text-center py-24">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Briefcase className="w-8 h-8 text-gray-300" />
            </div>
            <h3 className="text-lg font-bold text-gray-700 mb-2">{t('portfolios.noProfiles') || 'Nenhum perfil encontrado'}</h3>
            <p className="text-gray-500 text-sm">{t('portfolios.tryFilters') || 'Tente ajustar os filtros ou a pesquisa.'}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
            {freelancers.map(f => (
              <FreelancerCard key={f.id} f={f} />
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-10 flex items-center justify-center gap-3">
            <button
              disabled={page <= 1}
              onClick={() => { const p = page - 1; setPage(p); load(q, skill, country, p); }}
              className="flex items-center gap-1 px-4 py-2 border border-gray-200 bg-white rounded-xl text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
            >
              <ChevronLeft className="w-4 h-4" /> {t('portfolios.prev') || 'Anterior'}
            </button>
            <span className="text-sm text-gray-500">{t('portfolios.page') || 'Página'} {page} {t('portfolios.of') || 'de'} {totalPages}</span>
            <button
              disabled={page >= totalPages}
              onClick={() => { const p = page + 1; setPage(p); load(q, skill, country, p); }}
              className="flex items-center gap-1 px-4 py-2 border border-gray-200 bg-white rounded-xl text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
            >
              {t('portfolios.next') || 'Seguinte'} <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}

function FreelancerCard({ f }: { f: PublicFreelancer }) {
  const name = `${f.firstName} ${f.lastName}`;
  const initials = `${f.firstName[0]}${f.lastName[0]}`.toUpperCase();
  const topPortfolio = f.portfolio[0];

  return (
    <Link href={`/freelancers/${f.profileSlug}`} className="group block">
      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden hover:shadow-lg hover:border-teal-200 transition-all duration-200">
        {/* Cover image → portfolio image → gradient placeholder */}
        <div className="relative h-40 bg-gradient-to-br from-teal-50 to-blue-50 overflow-hidden">
          {f.coverPhoto ? (
            <img src={f.coverPhoto} alt={name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
          ) : topPortfolio?.imageUrl ? (
            <img src={topPortfolio.imageUrl} alt={topPortfolio.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <div className="w-16 h-16 rounded-full bg-teal-600 flex items-center justify-center text-2xl font-black text-white">
                {initials}
              </div>
            </div>
          )}
          {/* Availability badge */}
          {f.isAvailableForWork && (
            <div className="absolute top-3 right-3 bg-green-500 text-white text-xs font-bold px-2 py-0.5 rounded-full shadow">
              Disponível
            </div>
          )}
          {/* Portfolio count badge */}
          {f._count.portfolio > 0 && (
            <div className="absolute bottom-3 left-3 bg-black/60 text-white text-xs font-semibold px-2 py-0.5 rounded-full backdrop-blur-sm">
              {f._count.portfolio} projeto{f._count.portfolio !== 1 ? 's' : ''}
            </div>
          )}
        </div>

        <div className="p-4">
          {/* Avatar + name row */}
          <div className="flex items-center gap-3 mb-2">
            {f.profilePhoto ? (
              <img src={f.profilePhoto} alt={name} className="w-9 h-9 rounded-full object-cover border-2 border-white shadow" />
            ) : (
              <div className="w-9 h-9 rounded-full bg-teal-600 flex items-center justify-center text-sm font-black text-white flex-shrink-0">
                {initials}
              </div>
            )}
            <div className="min-w-0">
              <div className="flex items-center gap-1.5 min-w-0">
                <div className="font-bold text-gray-900 text-sm truncate">{name}</div>
                {f.isVerifiedProfessional && <VerificationBadge size="sm" showLabel={false} />}
              </div>
              {(f.city || f.country) && (
                <div className="flex items-center gap-1 text-xs text-gray-500">
                  <MapPin className="w-3 h-3" />
                  {[f.city, f.country].filter(Boolean).join(', ')}
                </div>
              )}
            </div>
          </div>

          {/* Headline */}
          {f.headline && (
            <p className="text-xs text-gray-600 line-clamp-2 mb-3">{f.headline}</p>
          )}

          {/* Skills */}
          {f.skills.length > 0 && (
            <div className="flex flex-wrap gap-1 mb-3">
              {f.skills.slice(0, 3).map((s, i) => (
                <span key={i} className="text-xs bg-teal-50 text-teal-700 font-medium px-2 py-0.5 rounded-full border border-teal-100">
                  {s.skill.name}
                </span>
              ))}
              {f.skills.length > 3 && (
                <span className="text-xs text-gray-400">+{f.skills.length - 3}</span>
              )}
            </div>
          )}

          {/* Salary expectation */}
          {(f.salaryExpectationMin || f.salaryExpectationMax) && (
            <div className="text-xs font-bold text-teal-600 border-t border-gray-100 pt-3 mt-1">
              {f.salaryCurrency} {(f.salaryExpectationMin ?? f.salaryExpectationMax)?.toLocaleString()}
              {f.salaryExpectationMax && f.salaryExpectationMin && f.salaryExpectationMax !== f.salaryExpectationMin ? `–${f.salaryExpectationMax.toLocaleString()}` : ''}
              <span className="font-normal text-gray-400"> /mês</span>
            </div>
          )}
        </div>
      </div>
    </Link>
  );
}
